import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCart } from "../../Redux/CartSlice";
import { useNavigate } from "react-router-dom";

import {
  getCartByUserId,
  updateCartOnServer,
} from "../../services/Cart/CartPage";

import PaymentMethod from "./PaymentMethod";
import Slots from "./Slots";

import { useLocationContext } from "../../context/LocationContext";
import { UserContext } from "../../context/UserContext";

import LoginPopup from "../../component/common/LoginPopup";
import { 
  X, Minus, Plus, Edit2, Ticket, Check, MapPin, 
  ShoppingBag, Trash2, Clock, ShieldCheck, Sparkles,
  FlaskConical, Package, ArrowRight
} from "lucide-react";

import fallbackImg from "../assets/logo/appstore.png";
import Address from "../../component/Profile/Address/Index";
import { toast } from "react-toastify";

/* ================= REUSABLE COUPON SUB-COMPONENT ================= */
const CouponSection = ({ userId, totalAmount, onApplyCoupon, appliedCoupon }) => {
  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const fetchAvailableCoupons = async () => {
      setCouponLoading(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || "https://admin.medrphalabs.com/api/";
        const response = await fetch(`${API_URL}Coupan/GetAllCoupon?userId=${userId}&labId=1`);
        if (response.ok) {
          const data = await response.json();
          setCoupons(data.MainCoupons || data.UserCoupons || []);
        }
      } catch (err) {
        console.error("❌ FETCH COUPONS ERROR:", err);
      } finally {
        setCouponLoading(false);
      }
    };

    fetchAvailableCoupons();
  }, [userId]);

  if (coupons.length === 0 && !couponLoading) return null;

  return (
    <div className="bg-white border border-rose-100 rounded-2xl p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100">
        <div className="flex items-center gap-2 text-red-600 font-bold text-xs sm:text-sm">
          <Ticket size={16} />
          <span className="uppercase tracking-wider font-extrabold">Available Offers & Coupons</span>
        </div>
        <span className="text-xs text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded-full">{coupons.length} Offers</span>
      </div>

      {couponLoading ? (
        <div className="py-3 text-center">
          <div className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs text-slate-500 mt-1">Finding best discounts for you...</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 max-h-[160px] overflow-y-auto pr-1 custom-scrollbar">
          {coupons.map((coupon, idx) => {
            const isApplied = appliedCoupon?.Code === coupon.Code;
            return (
              <div 
                key={coupon.Id || idx}
                className={`flex items-center justify-between p-2.5 rounded-xl border transition-all ${
                  isApplied 
                    ? 'border-emerald-500 bg-emerald-50/70 shadow-xs' 
                    : 'border-dashed border-slate-200 bg-slate-50/70 hover:bg-rose-50/40 hover:border-red-300'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs font-black bg-white px-2 py-0.5 border border-slate-200 rounded-md text-slate-900 shadow-2xs">
                      {coupon.Code}
                    </span>
                    <span className="text-xs font-extrabold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md">
                      {coupon.DiscountValue}% OFF
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1 truncate font-medium">
                    {coupon.Description || "Extra instant savings on your health tests"}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => onApplyCoupon(coupon)}
                  className={`text-xs font-extrabold px-3 py-1 rounded-lg transition-all ml-2 flex items-center gap-1 shrink-0 cursor-pointer ${
                    isApplied 
                      ? 'bg-emerald-600 text-white shadow-xs' 
                      : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow-xs'
                  }`}
                >
                  {isApplied ? (
                    <>
                      <Check size={12} /> Applied
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ================= MAIN CART DRAWER COMPONENT ================= */
export default function CartDrawer({ onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cartItems = useSelector(
    (state) => state.cart.items || []
  );

  const [cartId, setCartId] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [showAddressList, setShowAddressList] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  /* --- COUPON SELECTION STATES FOR CALCULATIONS --- */
  const [appliedCoupon, setAppliedCoupon] = useState(null); 
  const [discountAmount, setDiscountAmount] = useState(0);

  const { user } = useContext(UserContext);
  const userId = user?.userId;

  const {
    address,
    loading: locationLoading,
    setAddress,
  } = useLocationContext();

  const requiresSlot = cartItems.some(
    (item) =>
      Number(item.categoryId) === 2 ||
      Number(item.categoryId) === 3
  );

  /* ================= ADDRESS ================= */
  const handleSelectAddress = (selectedAddress) => {
    setAddress(selectedAddress);
    toast.success("Address selected successfully!");
    setShowAddressList(false);
  };

  /* ================= FETCH CART ================= */
  useEffect(() => {
    if (!userId) {
      setShowPopup(true);
      return;
    }

    setShowPopup(false);

    const fetchCart = async () => {
      try {
        const response = await getCartByUserId(userId);
        const { cartId, items } = response || {};
        setCartId(cartId || 0);
        dispatch(setCart(items || []));
      } catch (err) {
        console.error("❌ FETCH CART ERROR:", err);
      }
    };

    fetchCart();
  }, [dispatch, userId, user]);

  /* ================= TOTAL & DISCOUNT CALCULATION ================= */
  useEffect(() => {
    const total = cartItems.reduce(
      (sum, i) => sum + Number(i.TotalPrice || i.price * i.quantity || 0),
      0
    );
    setTotalAmount(total);

    if (appliedCoupon) {
      const discValue = Number(appliedCoupon.DiscountValue || 0);
      const calculatedDiscount = (total * discValue) / 100;
      setDiscountAmount(calculatedDiscount);
    } else {
      setDiscountAmount(0);
    }
  }, [cartItems, appliedCoupon]);

  /* ================= PARENT COUPON HANDLER ================= */
  const handleCouponToggle = (coupon) => {
    if (appliedCoupon?.Code === coupon.Code) {
      setAppliedCoupon(null);
      setDiscountAmount(0);
    } else {
      setAppliedCoupon(coupon);
      const discValue = Number(coupon.DiscountValue || 0);
      const calculatedDiscount = (totalAmount * discValue) / 100;
      setDiscountAmount(calculatedDiscount);
    }
  };

  const handleClose = () => {
    onClose && onClose();
  };

  /* ================= UPDATE QUANTITY FOR PRODUCTS, TESTS & PACKAGES ================= */
  const handleUpdateQuantity = async (item, change) => {
    const currentQty = item.quantity || 1;
    const newQuantity = currentQty + change;
    
    if (newQuantity < 1) {
      await handleRemove(item);
      return;
    }

    setUpdatingItemId(item.productId);

    if (!userId) {
      // Guest local cart update
      const updatedItems = cartItems.map((i) => {
        if (Number(i.productId) === Number(item.productId) && Number(i.categoryId) === Number(item.categoryId)) {
          const unitPrice = Number(i.price || 0);
          return {
            ...i,
            quantity: newQuantity,
            TotalPrice: unitPrice * newQuantity,
          };
        }
        return i;
      });
      dispatch(setCart(updatedItems));
      setUpdatingItemId(null);
      return;
    }

    try {
      const payload = {
        userId,
        productId: item.productId,
        categoryId: item.categoryId,
        totalQuantity: change,
        price: item.price,
        discount: item.discount || 0,
        coupon: appliedCoupon ? appliedCoupon.Code : "",
      };

      await updateCartOnServer(payload);
      const { items } = await getCartByUserId(userId);
      dispatch(setCart(items || []));
    } catch (err) {
      console.error("❌ UPDATE QUANTITY ERROR:", err);
    } finally {
      setUpdatingItemId(null);
    }
  };

  /* ================= REMOVE ================= */
  const handleRemove = async (item) => {
    if (!userId) {
      const updated = cartItems.filter(
        (i) => !(Number(i.productId) === Number(item.productId) && Number(i.categoryId) === Number(item.categoryId))
      );
      dispatch(setCart(updated));
      return;
    }

    try {
      const payload = {
        userId,
        productId: item.productId,
        categoryId: item.categoryId,
        totalQuantity: -1,
        price: item.price,
        discount: item.discount || 0,
        coupon: "",
      };

      await updateCartOnServer(payload);
      const { items } = await getCartByUserId(userId);
      dispatch(setCart(items || []));
    } catch (err) {
      console.error("❌ REMOVE ERROR:", err);
    }
  };

  /* ================= GROUP ITEMS ================= */
  const testServices = cartItems.filter((i) => Number(i.categoryId) === 2);
  const packages = cartItems.filter((i) => Number(i.categoryId) === 3);
  const products = cartItems.filter((i) => Number(i.categoryId) === 1 || (!i.categoryId || (i.categoryId !== 2 && i.categoryId !== 3)));

  /* ================= PRODUCT ITEM UI ================= */
  const renderProductItem = (item) => {
    const isUpdating = updatingItemId === item.productId;
    const quantity = item.quantity || 1;
    const finalPrice = Number(item.TotalPrice || item.price * quantity);
    const itemOriginalPrice = item.originalPrice ? Number(item.originalPrice) * quantity : null;
    const savings = itemOriginalPrice && itemOriginalPrice > finalPrice ? itemOriginalPrice - finalPrice : (item.discount ? Number(item.discount) * quantity : 0);
    const itemName = (item.name || "Product").trim();

    return (
      <div key={`${item.productId}-${item.categoryId}`} className="bg-white border border-slate-200/90 rounded-2xl p-3 shadow-2xs hover:border-red-300 transition-all">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center p-1">
            <img
              src={item.image ? item.image.startsWith("http") ? item.image : `https://admintest.medrphalabs.com/TestMasterImage/${item.image}` : fallbackImg}
              className="w-full h-full object-contain"
              alt="product"
              onError={(e) => { e.target.src = fallbackImg; }}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1.5">
              <h6 className="text-xs sm:text-sm font-extrabold text-slate-900 truncate leading-tight">{itemName}</h6>
              <button 
                onClick={() => handleRemove(item)}
                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                title="Remove item"
              >
                <Trash2 size={13} />
              </button>
            </div>

            <div className="flex items-center justify-between mt-1.5 pt-1 border-t border-slate-100">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-xs sm:text-sm font-black text-slate-900">
                  ₹{finalPrice.toLocaleString()}
                </span>
                {itemOriginalPrice && itemOriginalPrice > finalPrice && (
                  <span className="text-xs line-through text-slate-400">
                    ₹{itemOriginalPrice.toLocaleString()}
                  </span>
                )}
                {savings > 0 && (
                  <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                    Save ₹{savings.toLocaleString()}
                  </span>
                )}
              </div>

              {/* Simple Remove button */}
              <button
                onClick={() => handleRemove(item)}
                className="px-2.5 py-1 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50 border border-red-200 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <Trash2 size={11} />
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ================= LAB TEST & PACKAGE ITEM UI ================= */
  const renderServiceItem = (item) => {
    const isPackage = Number(item.categoryId) === 3;
    const quantity = item.quantity || 1;
    const finalItemPrice = Number(item.TotalPrice || item.price * quantity || 0);
    const itemOriginalPrice = item.originalPrice ? Number(item.originalPrice) * quantity : null;
    const savings = itemOriginalPrice && itemOriginalPrice > finalItemPrice ? itemOriginalPrice - finalItemPrice : (item.discount ? Number(item.discount) * quantity : 0);
    const serviceName = (item.name || "Diagnostic Service").trim();

    return (
      <div key={`${item.productId}-${item.categoryId}`} className="bg-white border border-slate-200/90 rounded-2xl p-3 shadow-2xs hover:border-red-300 transition-all relative group">
        <div className="flex items-start gap-3">
          {/* Service Icon / Thumbnail */}
          <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-100/80 overflow-hidden shrink-0 flex items-center justify-center text-red-600 mt-0.5 shadow-2xs">
            {isPackage ? <Package size={20} /> : <FlaskConical size={20} />}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider ${
                    isPackage 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {isPackage ? 'PACKAGE' : 'LAB TEST'}
                  </span>
                  <span className="text-[10px] font-bold text-emerald-700 flex items-center gap-0.5">
                    <ShieldCheck size={11} /> NABL Verified
                  </span>
                </div>
                <h6 className="text-xs sm:text-sm font-extrabold text-slate-900 line-clamp-2 group-hover:text-red-600 transition-colors mt-1 leading-snug">
                  {serviceName}
                </h6>
              </div>

              <button 
                onClick={() => handleRemove(item)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0 cursor-pointer"
                title="Remove item"
              >
                <Trash2 size={13} />
              </button>
            </div>

            {/* Turnaround & Inclusions */}
            <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
              <span className="flex items-center gap-1 text-slate-500 font-medium">
                <Clock size={11} className="text-red-500" /> Digital Report within 24 hrs
              </span>
            </div>

            {/* Price & Action Row (Matches App style) */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-xs sm:text-sm font-black text-slate-900">
                  ₹{finalItemPrice.toLocaleString()}
                </span>
                {itemOriginalPrice && itemOriginalPrice > finalItemPrice && (
                  <span className="text-xs line-through text-slate-400">
                    ₹{itemOriginalPrice.toLocaleString()}
                  </span>
                )}
                {savings > 0 && (
                  <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                    Save ₹{savings.toLocaleString()}
                  </span>
                )}
              </div>

              {/* Simple Remove Button */}
              <button
                onClick={() => handleRemove(item)}
                className="px-3 py-1 rounded-xl text-xs font-bold text-red-600 hover:bg-red-600 hover:text-white border border-red-200 transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
                title="Remove from cart"
              >
                <Trash2 size={11} />
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Section = ({ title, icon: Icon, count, children }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={14} className="text-red-600" />}
          <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider">{title}</h4>
        </div>
        {count !== undefined && (
          <span className="text-xs font-extrabold text-slate-600 bg-slate-200/80 px-2 py-0.5 rounded-full">
            {count}
          </span>
        )}
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-2xs z-50 transition-opacity" 
        onClick={handleClose} 
      />

      {/* Drawer */}
      <div 
        onClick={(e) => e.stopPropagation()} 
        className="fixed right-0 top-0 w-full sm:w-[440px] md:w-[460px] h-full bg-[#F8FAFC] z-50 flex flex-col shadow-2xl transition-all duration-300 font-sans"
      >
        {/* HEADER */}
        <div className="px-4 py-3 bg-white border-b border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 text-white flex items-center justify-center shadow-xs">
                <ShoppingBag size={16} />
              </div>
              <div>
                <h5 className="text-sm sm:text-base font-black text-slate-900 leading-tight">My Diagnostic Cart</h5>
                <span className="text-xs text-slate-500 font-medium">{cartItems.length} {cartItems.length === 1 ? 'Item' : 'Items'} Selected</span>
              </div>
            </div>

            <button 
              onClick={handleClose} 
              className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors cursor-pointer"
            >
              <X size={17} />
            </button>
          </div>

          {/* Patient Collection Address Banner */}
          <div className="bg-red-50/60 border border-red-200/80 rounded-2xl px-3 py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin size={15} className="text-red-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] font-black text-red-900 uppercase tracking-wider">Sample Collection Address</p>
                <p className="text-xs text-slate-800 font-bold truncate">
                  {locationLoading ? "Detecting location..." : address || "No address selected"}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowAddressList(true)}
              className="flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-red-50 text-red-600 rounded-xl text-xs font-bold transition-all border border-red-200 shrink-0 shadow-2xs cursor-pointer"
            >
              <Edit2 size={11} />
              Change
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-[#F8FAFC] space-y-3 custom-scrollbar">
          {/* Empty State */}
          {cartItems.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <div className="w-16 h-16 bg-red-50 text-red-400 rounded-full flex items-center justify-center mb-3 shadow-inner">
                <ShoppingBag size={28} />
              </div>
              <h4 className="text-sm font-bold text-gray-800">Your Cart is Empty</h4>
              <p className="text-xs text-gray-500 mt-1 max-w-xs leading-relaxed">
                Explore our full range of certified pathology tests and health checkup packages with home collection.
              </p>
              <button
                onClick={() => {
                  handleClose();
                  navigate("/all-tests");
                }}
                className="mt-4 inline-flex items-center gap-1.5 px-5 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-xl shadow-md hover:from-red-600 hover:to-red-700 transition-all"
              >
                <span>Browse Health Tests</span>
                <ArrowRight size={12} />
              </button>
            </div>
          )}

          {/* Slot Scheduler Component */}
          {requiresSlot && <Slots />}

          {/* Items Listed By Category */}
          {products.length > 0 && (
            <Section title="Medicines & Products" icon={ShoppingBag} count={products.length}>
              {products.map(renderProductItem)}
            </Section>
          )}

          {testServices.length > 0 && (
            <Section title="Pathology & Lab Tests" icon={FlaskConical} count={testServices.length}>
              {testServices.map(renderServiceItem)}
            </Section>
          )}

          {packages.length > 0 && (
            <Section title="Health Packages" icon={Package} count={packages.length}>
              {packages.map(renderServiceItem)}
            </Section>
          )}

          {/* Available Offers & Coupons */}
          {cartItems.length > 0 && (
            <CouponSection 
              userId={userId} 
              totalAmount={totalAmount} 
              onApplyCoupon={handleCouponToggle} 
              appliedCoupon={appliedCoupon} 
            />
          )}

          {/* Applied Coupon Banner */}
          {appliedCoupon && (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 text-green-800 text-xs px-2.5 py-1.5 rounded-lg shadow-2xs">
              <div className="flex items-center gap-1.5">
                <Sparkles size={13} className="text-green-600" />
                <span className="text-[11px]">Coupon <b>{appliedCoupon.Code}</b> applied! Saved <b>₹{discountAmount.toFixed(2)}</b></span>
              </div>
              <button 
                onClick={() => { setAppliedCoupon(null); setDiscountAmount(0); }} 
                className="text-red-600 hover:text-red-800 font-bold underline text-[10px]"
              >
                Remove
              </button>
            </div>
          )}

          {/* Payment Method & Order Summary */}
          {cartItems.length > 0 && (
            <PaymentMethod
              totalAmount={totalAmount - discountAmount}
              totalDiscount={discountAmount}
              cartItems={cartItems}
              userId={userId}
              appliedCouponCode={appliedCoupon?.Code || ""}
            />
          )}
        </div>
      </div>

      {/* Address Selection Modal */}
      {showAddressList && (
        <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-2xs flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl p-5 max-h-[90vh] overflow-y-auto relative shadow-2xl">
            <button 
              onClick={() => setShowAddressList(false)} 
              className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X size={18} />
            </button>
            <Address onSelectAddress={handleSelectAddress} />
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </>
  );
}